import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

// Customer Pipeline board's status dot (story #227) — red/yellow/green,
// driven by pipelineDotResolver.js's `{ state, title, detail }` the same
// "caller derives state, component just renders it" split as ProgressDot.js.
// Native title covers hover (kept to the short, generic label so the OS
// tooltip doesn't sprawl); a click/tap also toggles a small popover with
// that same label plus the specific blocker (`detail`, e.g. `"Left
// Message" — logged 3 days ago`), since title alone doesn't show on tap
// (touch devices) and was too generic to say *why* on its own.
//
// The popover renders through a portal into document.body rather than as a
// normal absolutely-positioned child: a Pipeline card sits inside two
// scrolling containers (its Stage column, and the board's own horizontal
// scroller), and setting overflow-y on the column implicitly makes its
// overflow-x auto too (CSS overflow spec) — so a same-DOM-position popover
// gets silently clipped the moment it pokes past either container's edge,
// which is exactly what happened near the left edge of a narrow column.
// Fixed positioning computed from the dot's own bounding rect sidesteps
// that regardless of which scroll container it's nested in.
const SIZE = { sm: 14, md: 18 };
const COLORS = { red: '#c62828', yellow: '#c98a1f', green: '#1e7e34' };
const POPOVER_WIDTH = 160;

function TrafficLightDot({ state, title, detail, size = 'sm' }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = btnRef.current?.getBoundingClientRect();
      if (!rect) return;
      const halfWidth = POPOVER_WIDTH / 2;
      const left = Math.min(Math.max(rect.left + rect.width / 2, halfWidth + 8), window.innerWidth - halfWidth - 8);
      setPos({ top: rect.bottom + 6, left });
    };
    place();
    const close = () => setOpen(false);
    // Any scroll (column, board, or page) or resize can move the dot out
    // from under a fixed-position popover — closing rather than
    // re-tracking keeps this simple, same tradeoff a native tooltip makes.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    const onDocClick = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('click', onDocClick);
    };
  }, [open]);

  const dim = SIZE[size] || SIZE.sm;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={title}
        style={{
          display: 'inline-block',
          width: dim,
          height: dim,
          borderRadius: '50%',
          background: COLORS[state] || COLORS.green,
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          verticalAlign: 'middle',
        }}
      />
      {open && pos && createPortal(
        <span
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: 'translateX(-50%)',
            background: '#1a1a1a',
            color: '#fff',
            fontSize: 11,
            lineHeight: 1.4,
            padding: '4px 8px',
            borderRadius: 4,
            whiteSpace: detail ? 'normal' : 'nowrap',
            width: detail ? POPOVER_WIDTH : undefined,
            textAlign: 'center',
            zIndex: 1000,
          }}
        >
          <div style={{ fontWeight: 600 }}>{title}</div>
          {detail && <div style={{ marginTop: 2, color: '#d8dde5' }}>{detail}</div>}
        </span>,
        document.body
      )}
    </>
  );
}

export default TrafficLightDot;
